import { useMemo } from "react";

import {
  FileExclamationOutlined,
  FileOutlined,
  DollarOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { Column, Pie } from "@ant-design/plots";
import {
  Col,
  Typography,
  Card,
  Statistic,
  Row,
  Tooltip,
  DatePicker,
} from "antd";

import dayjs from "../../../config/dayjs";
import { formatCompactRM, formatRM } from "../../../utils/numberFormat";
import { styles } from "../styles/dashboardStyles";
import { Dashboard } from "../type/dashboard";

const { Text } = Typography;

interface BranchAdminDashboardProps {
  dashboard: Dashboard;
  isSerembanBranch?: boolean;
  selectedYear?: number;
  onYearChange?: (date: number) => void;
}

const BranchAdminDashboardCards = ({
  dashboard,
  isSerembanBranch,
  selectedYear,
  onYearChange,
}: BranchAdminDashboardProps) => {
  const cardStyle = {
    ...styles.card,
    margin: "10px 0",
    height: 200,
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    border: "1px solid #f0f0f0",
  };

  const pieData = useMemo(() => {
    const raw = dashboard?.fileCounts as any;
    const totalFiles = raw?.totalFiles ?? 0;
    const completedFiles = raw?.completedFiles ?? 0;
    const incompleteFiles = Math.max(totalFiles - completedFiles, 0);
    return [
      { type: "Completed", value: completedFiles },
      { type: "Incomplete", value: incompleteFiles },
    ];
  }, [dashboard]);

  return (
    <>
      <Col xs={24} sm={12} lg={8} xl={8}>
        <Card
          // hoverable
          size="small"
          title={
            <div style={{ display: "flex", alignItems: "center" }}>
              <DollarOutlined
                style={{
                  fontSize: "18px",
                  color: "#52c41a",
                  marginRight: "8px",
                }}
              />
              <span>SPA Value ({dashboard?.month})</span>
            </div>
          }
          style={cardStyle}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              padding: "16px",
            }}
          >
            <Statistic
              value={dashboard?.totalSPAPrice || 0}
              valueStyle={{
                color: "#52c41a",
                fontSize: "28px",
                fontWeight: "bold",
              }}
              // prefix="RM"
              formatter={(value) => formatCompactRM(Number(value))}
              valueRender={() => (
                <Tooltip title={formatRM(dashboard?.totalSPAPrice || 0)}>
                  {formatCompactRM(dashboard?.totalSPAPrice || 0)}
                </Tooltip>
              )}
            />
            <Text
              type="secondary"
              style={{
                fontSize: "14px",
                marginTop: "8px",
                textAlign: "center",
              }}
            >
              Total SPA value for the month
            </Text>
          </div>
        </Card>
      </Col>

      <Col xs={24} sm={12} lg={8} xl={8}>
        <Card
          // hoverable
          size="small"
          title={
            <div style={{ display: "flex", alignItems: "center" }}>
              <FileOutlined
                style={{
                  fontSize: "18px",
                  color: "#1890ff",
                  marginRight: "8px",
                }}
              />
              <span>Monthly Files ({dashboard?.month})</span>
            </div>
          }
          style={cardStyle}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              padding: "16px",
            }}
          >
            <Statistic
              value={dashboard?.totalFileByMonthByBranch || 0}
              valueStyle={{
                color: "#1890ff",
                fontSize: "28px",
                fontWeight: "bold",
              }}
              prefix={<FileOutlined />}
            />
            <Text
              type="secondary"
              style={{
                fontSize: "14px",
                marginTop: "8px",
                textAlign: "center",
              }}
            >
              Total files this month
            </Text>
          </div>
        </Card>
      </Col>

      <Col xs={24} sm={12} lg={8} xl={8}>
        <Card
          // hoverable
          size="small"
          title={
            <div style={{ display: "flex", alignItems: "center" }}>
              <FileExclamationOutlined
                style={{
                  fontSize: "18px",
                  color: "#ff4d4f",
                  marginRight: "8px",
                }}
              />
              <span>All Incomplete Files</span>
            </div>
          }
          style={cardStyle}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              padding: "16px",
            }}
          >
            <Statistic
              value={dashboard?.totalFileNotCompleted || 0}
              valueStyle={{
                color: "#ff4d4f",
                fontSize: "28px",
                fontWeight: "bold",
              }}
              prefix={<FileExclamationOutlined />}
            />
            <Text
              type="secondary"
              style={{
                fontSize: "14px",
                marginTop: "8px",
                textAlign: "center",
              }}
            >
              Files requiring completion
            </Text>
          </div>
        </Card>
      </Col>

      <Col xs={24}>
        <Card
          size="small"
          title={
            <div style={{ display: "flex", alignItems: "center" }}>
              <ClockCircleOutlined
                style={{
                  fontSize: "18px",
                  color: "#1890ff",
                  marginRight: "8px",
                }}
              />
              <span>Completion Date Ranges</span>
            </div>
          }
          style={{
            ...cardStyle,
            height: "auto",
            minHeight: "200px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              padding: "16px",
            }}
          >
            <Row gutter={[24, 24]} style={{ width: "100%" }}>
              <Col xs={24} sm={8}>
                <div
                  style={{
                    textAlign: "center",
                    padding: "16px",
                    background: "rgba(82, 196, 26, 0.05)",
                    borderRadius: "8px",
                  }}
                >
                  <Statistic
                    value={dashboard?.completionDateRanges?.lessThan30Days || 0}
                    valueStyle={{
                      color: "#52c41a",
                      fontSize: "32px",
                      fontWeight: "bold",
                    }}
                    prefix={<CheckCircleOutlined />}
                  />
                  <Text
                    type="secondary"
                    style={{
                      fontSize: "16px",
                      display: "block",
                      textAlign: "center",
                      marginTop: "8px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {"<"} 30 days
                  </Text>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div
                  style={{
                    textAlign: "center",
                    padding: "16px",
                    background: "rgba(250, 173, 20, 0.05)",
                    borderRadius: "8px",
                  }}
                >
                  <Statistic
                    value={
                      dashboard?.completionDateRanges?.between30And90Days || 0
                    }
                    valueStyle={{
                      color: "#faad14",
                      fontSize: "32px",
                      fontWeight: "bold",
                    }}
                    prefix={<WarningOutlined />}
                  />
                  <Text
                    type="secondary"
                    style={{
                      fontSize: "16px",
                      display: "block",
                      textAlign: "center",
                      marginTop: "8px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    30-90 days
                  </Text>
                </div>
              </Col>
              <Col xs={24} sm={8}>
                <div
                  style={{
                    textAlign: "center",
                    padding: "16px",
                    background: "rgba(255, 77, 79, 0.05)",
                    borderRadius: "8px",
                  }}
                >
                  <Statistic
                    value={dashboard?.completionDateRanges?.moreThan90Days || 0}
                    valueStyle={{
                      color: "#ff4d4f",
                      fontSize: "32px",
                      fontWeight: "bold",
                    }}
                    prefix={<FileExclamationOutlined />}
                  />
                  <Text
                    type="secondary"
                    style={{
                      fontSize: "16px",
                      display: "block",
                      textAlign: "center",
                      marginTop: "8px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {">"} 90 days
                  </Text>
                </div>
              </Col>
            </Row>
          </div>
        </Card>
      </Col>

      {/* check only show if user is in seremban branch */}
      {isSerembanBranch && (
        <Col xs={24}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} lg={24} xl={12}>
              <Card
                size="small"
                title={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      padding: "0 0 16px 0",
                    }}
                  >
                    <span>SPA Price Overview</span>
                    <DatePicker
                      picker="year"
                      value={dayjs(`${selectedYear}-01-01`)}
                      onChange={(date) =>
                        onYearChange &&
                        onYearChange(date ? date.year() : dayjs().year())
                      }
                      allowClear={false}
                      format="YYYY"
                      style={{ width: 120, borderRadius: "6px" }}
                      suffixIcon={
                        <CalendarOutlined style={{ color: "#1890ff" }} />
                      }
                    />
                  </div>
                }
                style={{
                  ...styles.card,
                  margin: "10px 0",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                  border: "1px solid #f0f0f0",
                  padding: 16,
                  minHeight: 400,
                }}
              >
                <Column
                  data={dashboard?.spaPriceInYear || []}
                  xField="month"
                  yField="total"
                  columnWidthRatio={0.5}
                  color="#52c41a"
                  xAxis={{
                    title: { text: "Month" },
                    label: { autoRotate: true, autoHide: true },
                  }}
                  yAxis={{
                    title: { text: "SPA Price (RM)" },
                    label: {
                      formatter: (v) => formatCompactRM(Number(v)),
                    },
                    nice: true,
                  }}
                  tooltip={{
                    formatter: (d) => ({
                      name: "SPA Price",
                      value: `RM ${Number(d.total).toLocaleString()}`,
                    }),
                  }}
                  autoFit
                  height={320}
                  label={false}
                  meta={{
                    month: { alias: "Month" },
                    total: { alias: "SPA Price" },
                  }}
                  interactions={[{ type: "element-active" }]}
                  padding={[24, 24, 48, 72]}
                  appendPadding={8}
                />
              </Card>
            </Col>
            <Col xs={24} sm={24} lg={16} xl={12}>
              <Card
                size="small"
                title={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      padding: "0 0 16px 0",
                    }}
                  >
                    <span>Files by Status</span>
                  </div>
                }
                style={{
                  ...styles.card,
                  margin: "10px 0",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                  border: "1px solid #f0f0f0",
                  padding: "20px 16px",
                  minHeight: 400,
                }}
              >
                <Pie
                  data={pieData}
                  angleField="value"
                  colorField="type"
                  radius={0.9}
                  label={{
                    type: "inner",
                    content: ({ percent }) => `${(percent * 100).toFixed(0)}%`,
                  }}
                  legend={{ position: "bottom" }}
                  tooltip={{
                    formatter: (d) => ({ name: d.type, value: d.value }),
                  }}
                  color={["#52c41a", "#ff4d4f"]}
                  interactions={[{ type: "element-active" }]}
                  statistic={{
                    title: false,
                    content: {
                      content: `${dashboard?.month}`,
                    },
                  }}
                  height={320}
                  padding={[24, 24, 48, 48]}
                  appendPadding={8}
                />
              </Card>
            </Col>
          </Row>
        </Col>
      )}
    </>
  );
};

export default BranchAdminDashboardCards;
